import React from 'react'
import { styled } from '@mui/material/styles'
import { GlassPanel, monoLabelSx, ResetGlyph } from '../shared'

export interface StudioOrbitSliderProps {
  /** 0-100, mapped onto `ORBIT_SWEEP_ALPHA` — see useStudioCameraRig's orbitAlphaPercent. */
  value: number
  onChange: (value: number) => void
  onCommit?: (value: number) => void
}

const Root = styled(GlassPanel)({
  position: 'absolute',
  right: 12,
  bottom: 16,
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  height: 44,
  padding: '0 8px 0 14px',
  borderRadius: 14,
})

/** Tiny uppercase mono caption — 1:1 with `StudioViewSwitcher`'s `Zoom` readout, so this chip reads as part of the same control family. */
const Label = styled('span')({ ...monoLabelSx, flexShrink: 0 })

const thumbSx = {
  width: 13,
  height: 13,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 5px rgba(0,0,0,0.5), 0 0 0 3px rgba(190,157,253,0.28)',
}

/** Same track/thumb treatment as the shared `StudioSlider`'s `RangeInput` — kept local (rather than reused) so this chip's track can claim most of the available width instead of splitting it with a 52px label column meant for wide side-panel rows. */
const Track = styled('input')({
  width: 120,
  flexShrink: 0,
  minWidth: 0,
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  height: 22,
  background: 'none',
  cursor: 'pointer',
  '&::-webkit-slider-runnable-track': { height: 3, borderRadius: 2, background: 'rgba(244,243,246,0.14)' },
  '&::-webkit-slider-thumb': { WebkitAppearance: 'none', marginTop: -5, ...thumbSx },
  '&::-moz-range-track': { height: 3, borderRadius: 2, background: 'rgba(244,243,246,0.14)' },
  '&::-moz-range-thumb': { border: 'none', ...thumbSx },
})

const Value = styled('span')({
  width: 32,
  flexShrink: 0,
  textAlign: 'right',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  color: 'var(--ds-night-ink-muted)',
})

const Sep = styled('span')({ width: 1, height: 22, flexShrink: 0, background: 'var(--ds-night-border)' })

/**
 * Wraps the reset button + its hover label. `position: relative` so the
 * label below can anchor off it directly — deliberately NOT MUI's
 * `Tooltip`, which portals its popper straight into `document.body`. This
 * chip sits right in the viewport's bottom-right corner, and that portaled
 * popper computing a position even a few px past the edge inflates
 * `document`'s scrollable area (a real, page-wide horizontal+vertical
 * scrollbar) until it closes. A plain `position: absolute` child here stays
 * inside `Stage`'s own `overflow: hidden` box like everything else in the
 * studio, so it can only ever be visually clipped, never expand the page.
 */
const ResetWrap = styled('span')({ position: 'relative', display: 'flex', flexShrink: 0 })

/** Same shape/behavior as `StudioToolbar`'s `ToolBtn` (transparent at rest, tinted on hover). */
const ResetBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 28,
  height: 28,
  borderRadius: 8,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  transition: 'all 140ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' },
  '& svg': { display: 'block' },
})

/** CSS-only hover label — see `ResetWrap`'s doc comment for why this replaces `Tooltip`. Anchored to the button's own right edge (not centered) so it grows away from the corner instead of toward it. */
const HoverLabel = styled('span')({
  position: 'absolute',
  right: 0,
  bottom: 'calc(100% + 8px)',
  padding: '5px 9px',
  borderRadius: 7,
  background: 'rgba(28,27,34,0.97)',
  border: '1px solid var(--ds-night-border)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--ds-night-ink)',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(3px)',
  transition: 'opacity 120ms ease, transform 120ms ease, visibility 120ms',
  zIndex: 41,
  [`${ResetWrap}:hover &, ${ResetWrap}:focus-within &`]: {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
})

/**
 * Bottom-right "look left/right" sweep — the 3D room's equivalent of the
 * old flat-wall Studio's Camera position slider, mirroring
 * `StudioViewSwitcher` on the opposite corner. For a procedural room this
 * sweeps the orbit camera's `alpha` across `ORBIT_SWEEP_ALPHA` (left wall →
 * right wall); for a Classic room (alpha locked) `useStudioCameraRig` pans
 * `target.x` across the room's own width instead — same control, same
 * `value`/`onChange` shape either way (see `orbitAlphaPercent`'s doc
 * comment). Orbit-view only — see Studio3DScreen's mount guard.
 *
 * Recenter here resets *pan* only — it's deliberately labeled "Recenter
 * pan," not just "Recenter," now that `StudioViewSwitcher`'s Zoom pill has
 * its own separate reset for zoom. Two clearly-labeled resets for two
 * different axes, not one ambiguous one.
 */
export function StudioOrbitSlider({ value, onChange, onCommit }: StudioOrbitSliderProps) {
  return (
    <Root>
      <Label>Pan</Label>
      <Track
        type='range'
        min={0}
        max={100}
        step={1}
        value={value}
        aria-label='Pan camera left/right'
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onPointerUp={(e) => onCommit?.(parseFloat((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
            onCommit?.(parseFloat((e.target as HTMLInputElement).value))
          }
        }}
      />
      <Value>{Math.round(value)}%</Value>
      <Sep />
      <ResetWrap>
        <ResetBtn type='button' onClick={() => onChange(50)} aria-label='Recenter pan'>
          <ResetGlyph />
        </ResetBtn>
        <HoverLabel>Recenter pan</HoverLabel>
      </ResetWrap>
    </Root>
  )
}
